import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { Checkbox } from 'src/components/ui/checkbox';
import { DealerManagementService } from 'src/services/dealer-management.service';
import Header from '../header/header';
import { IUser } from 'src/utils/interfaces/user';
import Spinner from 'src/components/ui/spinner';
import TooltipIcon from 'src/components/ui/customTooltip';
import { Types } from 'src/views/pages/system-admin/dealer-security-maintenance';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import './styles.scss';

interface IPropsTableItem {
    FIRSTNAME: String;
    LASTNAME: String;
    LOGIN: String;
    PARTYID: Number;
    PERMISSIONEXISTS: Number;
    STATUS: Number;
    DEALERIDS: String;
    STATUSTEXT: String;
    ID: string;
}

const SelectedUserRow = memo(function SelectedUserRow({
    user,
}: {
    user: IPropsTableItem;
}) {
    const dealerIds = useMemo(() => {
        const raw = String(user.DEALERIDS || '');
        return raw ? raw.split(',') : [];
    }, [user.DEALERIDS]);

    return (
        <li
            key={user.ID}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <span>
                {user.FIRSTNAME} {user.LASTNAME}
            </span>
            <span>{user.LOGIN}</span>
            <span>
                {dealerIds.map((ele, key) => (
                    <span key={key} style={{ marginRight: '5px' }}>
                        {ele}
                    </span>
                ))}
            </span>
        </li>
    );
});

const UserMigration = () => {
    const selectedUsers = useSelector(
        (state: IUser) => state.dsmSearch.selectedUsers,
        shallowEqual,
    );

    const searchType = useSelector(
        (state: IUser) => state.dsmSearch.searchType,
    );
    const searchValue = useSelector(
        (state: IUser) => state.dsmSearch.searchValue,
    );
    const dealerDataInfo = useSelector((state: IUser) => state.dealerDataInfo);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<true | false | null>(null);

    useEffect(() => {
        if (!selectedUsers?.length || !dealerDataInfo) {
            navigate('/dashboard/systemadmin/dealer-security-maintenance');
        }
    }, [selectedUsers?.length, dealerDataInfo, navigate]);

    const backClickHandler = useCallback(() => {
        navigate('/dashboard/systemadmin/dealer-security-maintenance/search');
    }, [navigate]);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                targetDealerId: Yup.string()
                    .required('This field is required')
                    .matches(
                        /^[A-Za-z0-9]{6}$/,
                        'Dealer Number must be exactly 6 characters long and alphanumeric',
                    ),
            }),
        [],
    );

    // PERF: construye el payload de usuarios solo cuando cambia selectedUsers
    const usersPayload = useMemo(() => {
        return (selectedUsers || []).map((ele: IPropsTableItem) => ({
            userId: ele.ID,
        }));
    }, [selectedUsers]);

    const formik = useFormik({
        initialValues: {
            targetDealerId: '',
            preserve: false,
        },
        validationSchema,
        validateOnChange: false,
        onSubmit: async (values) => {
            const service = new DealerManagementService();
            try {
                setLoading(true);
                setStatus(null);

                if (searchType === Types.DealerNumber) {
                    await service.migrateUserDSMWithDealerId(
                        searchValue,
                        values.targetDealerId,
                        usersPayload,
                        values.preserve,
                    );
                } else {
                    await service.migrateUserDSM(
                        values.targetDealerId,
                        usersPayload,
                        values.preserve,
                    );
                }

                setStatus(true);
            } catch {
                setStatus(false);
            } finally {
                setLoading(false);
            }
        },
    });

    const {
        values,
        setFieldValue,
        errors,
        touched,
        handleSubmit,
        handleChange,
        handleBlur,
        isSubmitting,
    } = formik;

    const onPreserveCheck = useCallback(
        (e: any) => {
            setFieldValue('preserve', e);
        },
        [setFieldValue],
    );

    const onSaveClick = useCallback(() => {
        handleSubmit();
    }, [handleSubmit]);

    return (
        <div className='sys-admin-user-migration'>
            <Header />
            <div className='sys-admin-user-migration-body u-mt-large'>
                {status !== null ? (
                    <Notification
                        content={
                            status === true
                                ? 'User(s) successfully migrated.'
                                : 'The dealer number you are migrating to is either in the same dealer group or cannot be found. Cannot move dealer number and selected users.'
                        }
                        type={
                            status === true
                                ? NotificationType.SUCCESS
                                : NotificationType.ERROR
                        }
                        visibility={status !== null}
                        setVisibility={() => setStatus(null)}
                    />
                ) : null}

                <div className='page-title'>
                    <h5>User Migration</h5>
                </div>
                <hr />

                <p>
                    The following users have been selected to be migrated to
                    another Dealer Number. Please enter a valid, Extranet Active
                    Dealer Number, enter comments and click the Submit button.
                    Check Preserve Permissions if you wish to keep the user(s)
                    current permissions intact (excluding the UA permission).
                </p>

                <span>
                    <strong>Selected User ID(s) prepared for migration:</strong>
                </span>

                <ul className='u-mt-small u-flex'>
                    {selectedUsers?.map((user: IPropsTableItem) => (
                        <SelectedUserRow key={user.ID} user={user} />
                    ))}
                </ul>

                <div className='o-fieldset__row u-mt-small'>
                    <div className='o-layout'>
                        <div className='o-layout__item  u-1/1  u-1/1@s'>
                            <div className='c-form-field'>
                                <label
                                    htmlFor='m1l7e063'
                                    className='c-form-field__label'
                                >
                                    Enter Dealer Number *
                                    <TooltipIcon text='Dealer number must have a valid format and be active in dSource.' />
                                </label>

                                <div className='c-input'>
                                    <div className='input-text-wrapper'>
                                        <input
                                            className='c-input__input'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type='text'
                                            value={values.targetDealerId}
                                            disabled={loading}
                                            name='targetDealerId'
                                            data-testid={'targetDealerId'}
                                        />
                                        {touched.targetDealerId &&
                                            !isSubmitting &&
                                            errors.targetDealerId && (
                                                <>
                                                    <br />
                                                    <div className='error-message'>
                                                        {errors.targetDealerId}
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='o-fieldset__row u-mt-small'>
                    <div className='o-layout'>
                        <div className='o-layout__item  u-1/1  u-1/1@s'>
                            <Checkbox
                                name={'Preserve Permissions'}
                                value={'preserve'}
                                onCheck={onPreserveCheck}
                                checked={values.preserve === true}
                                allBlack={true}
                            />
                            <TooltipIcon text='Check this box to preserve the current permissions of the selected users. Leaving this check box unchecked will strip the users of all permissions, and they will need to be manually re-established.' />
                        </div>
                    </div>
                </div>

                <div className='o-fieldset__row u-mt-large'>
                    <div className='o-layout'>
                        <div className='o-layout__item  u-1/1  u-1/1@s'>
                            <div className='o-fieldset__row button-group'>
                                <button
                                    className={
                                        'c-btn c-btn--secondary u-mr-small'
                                    }
                                    type='button'
                                    onClick={backClickHandler}
                                >
                                    <span className='c-btn__text'>
                                        Discard Changes
                                    </span>
                                </button>

                                <button
                                    className={'c-btn'}
                                    type='button'
                                    onClick={onSaveClick}
                                    disabled={loading || status === true}
                                >
                                    <span className='c-btn__text'>
                                        {loading ? (
                                            <div className='spinner-container'>
                                                <Spinner />
                                            </div>
                                        ) : (
                                            'Save'
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserMigration;
